import { deleteRepository } from '@gonfalon/openapi/operations/deleteRepository';
import { getBranch } from '@gonfalon/openapi/operations/getBranch';
import { getExtinctions } from '@gonfalon/openapi/operations/getExtinctions';
import { getRepositories, type GetRepositoriesQueryParams } from '@gonfalon/openapi/operations/getRepositories';
import { getRepository } from '@gonfalon/openapi/operations/getRepository';
import { getStatistics, type GetStatisticsQueryParams } from '@gonfalon/openapi/operations/getStatistics';
import { patchRepository } from '@gonfalon/openapi/operations/patchRepository';
import { postRepository } from '@gonfalon/openapi/operations/postRepository';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

export const codeRefsRepositoriesQuery = createQueryOptions(getRepositories);

export const codeRefsRepositoryQuery = createQueryOptions(getRepository);

export const codeRefsBranchQuery = createQueryOptions(getBranch);

export const codeRefsExtinctionsQuery = createQueryOptions(getExtinctions);

/**
 * Exported for the legacy Redux write path in `#actions/integrations` and `#actions/webhooks`, which
 * still owns these mutations while the integrations page is mid-migration (REL-15378). The Redux
 * page reads its own store, so without this the react-query page shows stale data for the length of
 * `staleTime` after any create, edit or delete.
 */
export async function invalidateRepositories() {
  const queryClient = getQueryClient();
  await queryClient.invalidateQueries({ queryKey: codeRefsRepositoriesQuery.partialQueryKey() });
  // `codeReferences` is the deprecated key factory below. Both are live until its call sites
  // move over, so a mutation has to invalidate each of them or one view goes stale.
  await queryClient.invalidateQueries({ queryKey: codeReferences.list._def });
}

export const useCreateRepository = createMutationHook(postRepository, {
  onSuccess: invalidateRepositories,
});

export const useUpdateRepository = createMutationHook(patchRepository, {
  onSuccess: async (_data, { repo }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: codeRefsRepositoryQuery.partialQueryKey({ repo }) });
    await invalidateRepositories();
  },
});

export const useDeleteRepository = createMutationHook(deleteRepository, {
  onSuccess: async (_data, { repo }) => {
    const queryClient = getQueryClient();
    queryClient.removeQueries({ queryKey: codeRefsRepositoryQuery.partialQueryKey({ repo }) });
    await invalidateRepositories();
  },
});

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const codeReferenceStatistics = createQueryKeys('code-reference-statistics', {
  list: ({ projectKey, params }: { projectKey: string; params?: GetStatisticsQueryParams }) => ({
    queryKey: params ? [projectKey, params] : [projectKey],
    queryFn: async () => reactQueryResponseAdapter(getStatistics({ projectKey, query: params })),
  }),
});

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const codeReferences = createQueryKeys('code-references', {
  list: ({ params }: { params?: GetRepositoriesQueryParams }) => ({
    queryKey: params ? ['repositories', params] : ['repositories'],
    queryFn: async () => reactQueryResponseAdapter(getRepositories({ query: params })),
  }),
});
